/**
 * Single source of truth for entity detail-page URLs.
 *
 * Why this exists: the app has ~25 places that build entity detail
 * paths inline (`/collections/${id}`, `/notes/${id}`,
 * `/attachments/${id}`).  Adding a new artifact subtype, splitting a
 * route, or supporting a workspace-scoped permalink would mean
 * touching every site.  Worse, the artifact-vs-attachment routing
 * decision (`/notes/<id>` for note + protocol, `/attachments/<id>`
 * for attachments) is non-obvious — easy to get wrong.
 *
 * Use `entityDetailPath()` for any new code that links to an entity.
 * Existing scattered call sites should migrate as they are touched.
 *
 * Mobile parity: `/m/notes/:noteId` and `/m/attachments/:attachmentId`
 * are both 30x redirects to the unified routes (see `App.tsx`).  No
 * device-specific branching needed here.
 */

import { getArtifactDetailRoute, isValidArtifactType } from '@bower/shared/constants/artifact';
import { ROUTES } from '../constants/routes';
import type { AclEntityType } from '../hooks/data/useEntityAccess';

export interface EntityDescriptor {
  entityType: AclEntityType;
  entityId: string;
  /**
   * Required when `entityType === 'ARTIFACT'` so the helper can
   * pick `/notes/<id>` (note + protocol) vs `/attachments/<id>`
   * (attachment).  Accepts the raw `Artifact.artifactType` string —
   * unknown values fall through to the note route, matching the
   * deployed default.  Ignored for `entityType === 'PROJECT'`.
   */
  artifactType?: string | null;
}

/**
 * Build the canonical detail-page URL for an entity.  Pure function;
 * safe to call from render, hooks, and event handlers alike.
 *
 * Examples:
 *   entityDetailPath({ entityType: 'PROJECT', entityId: 'p1' })
 *     → '/collections/p1'
 *   entityDetailPath({ entityType: 'ARTIFACT', entityId: 'a1', artifactType: 'note' })
 *     → '/notes/a1'
 *   entityDetailPath({ entityType: 'ARTIFACT', entityId: 'a1', artifactType: 'attachment' })
 *     → '/attachments/a1'
 *   entityDetailPath({ entityType: 'ARTIFACT', entityId: 'a1', artifactType: 'protocol' })
 *     → '/notes/a1'   (protocols share the note detail page)
 */
export function entityDetailPath(entity: EntityDescriptor): string {
  if (entity.entityType === 'PROJECT') {
    return ROUTES.collection(entity.entityId);
  }
  const subtype = entity.artifactType?.toLowerCase();
  // Use the registry's detailRoutePath for known artifact types. This means
  // adding a new artifact type with a unique route only requires a registry
  // entry — no code change here.
  if (subtype && isValidArtifactType(subtype)) {
    const route = getArtifactDetailRoute(subtype, entity.entityId);
    if (route) return route;
  }
  // Unknown subtypes fall through to /notes/:id — accidental new artifact
  // types still resolve to a working page rather than a 404 while migrations
  // land. The registry-path above ensures known types never reach this fallback.
  return ROUTES.note(entity.entityId);
}
